import DOMPurify from 'dompurify'

// Adjusted from https://stackoverflow.com/a/61511955/2302835
export async function waitForElement<TElement extends Element>(
  selector: string,
): Promise<TElement | null> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector<TElement>(selector))
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect()
        resolve(document.querySelector<TElement>(selector))
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  })
}

export function canScrollRight(element: Element) {
  // Note we use Math.ceil() on scrollLeft to account for the scrollLeft
  // sometimes being 0.5 pixel less when using the Chrome DevTools
  return (
    Math.ceil(element.scrollLeft) + element.clientWidth < element.scrollWidth
  )
}

// Highlights a table row element momentarily
export function highlightTableRowElement(
  tableRow: Element | null,
  options?: { duration: number },
) {
  if (!tableRow) {
    console.warn('No table row element found to highlight.')
    return
  }

  const defaultOptions = { duration: 6000 }
  const duration = options?.duration || defaultOptions.duration

  tableRow.classList.add('table-row--pulse')

  setTimeout(() => {
    tableRow.classList.remove('table-row--pulse')
  }, duration)
}

/**
 * Use to sanitize user input where safe HTML tags should be allowed.
 *
 * The list of allowed tags and attributes can be found by inspecting the
 * exported `html` object in the following places:
 * - https://github.com/cure53/DOMPurify/blob/main/src/tags.ts
 * - https://github.com/cure53/DOMPurify/blob/main/src/attrs.ts
 */
export function sanitizeHtml(str: string) {
  return DOMPurify.sanitize(str)
}

/**
 * Use for untrusted HTML that will be rendered inside an isolated, sandboxed
 * frame (see `SandboxedHtml`) rather than inlined into our own document.
 *
 * `style` elements are deliberately kept — the frame has its own document, so
 * the content's CSS can't reach our UI, and emails need it to render the way
 * they were sent. Form controls go, since there's nothing to submit to.
 */
export function sanitizeFramedHtml(str: string) {
  return DOMPurify.sanitize(str, {
    FORBID_TAGS: ['button', 'form', 'input', 'select', 'textarea'],
  })
}

/**
 * Use to sanitize user input where no HTML should be allowed.
 */
export function sanitizeText(str: string) {
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  })
}

/**
 * Use this helper to fetch CSS variable values when a direct lookup doesn't
 * work (e.g., inside Chart.js charts).
 *
 * Note we intentionally require the caller to pass the "--" prefix so that
 * we can more easily find all usages of particular variables via our IDE/editor.
 */
export function getCssVariable(variableName: string) {
  const value = window
    .getComputedStyle(document.body)
    .getPropertyValue(variableName)

  if (!value) {
    console.warn(`Variable ${variableName} not found`)
  }

  return value
}
